import type { NarrativeForm } from './schema';
import { getCompanyFinancialData, getDefaultValuatorData } from './calculation';
import type { Company } from '../hooks/companyFragment';
import type { CreateValuationInput } from '../hooks/useCreatePublicNarrative';
import type { UpdateValuationInput } from '../hooks/useUpdatePublicNarrative';

type TransformFormValuesToValuationProps = {
  values: NarrativeForm;
};

export const transformFormValuesToValuation = ({
  values,
}: TransformFormValuesToValuationProps) => {
  const {
    discountRatePercentage,
    fairValue,
    priceEarningsMultiple,
    profitMarginPercentage,
    revenuePercentage,
    timePeriod,
    futureSharesOutstanding,
  } = values;
  return {
    discountRate: discountRatePercentage,
    fairValue,
    futurePePercentageChange: priceEarningsMultiple,
    profitMarginPercentageChange: profitMarginPercentage,
    revenuePercentageChange: revenuePercentage,
    futureSharesOutstanding,
    timeframe: timePeriod,
  } satisfies CreateValuationInput | UpdateValuationInput;
};

export const transformValuationToFormValues = (
  valuation: {
    fairValue: number | null;
    futureSharesOutstanding: number | null;
    discountRate: number | null;
    futurePePercentageChange: number | null;
    profitMarginPercentageChange: number | null;
    revenuePercentageChange: number | null;
    timeframe: number | null;
  },
  company: Company
): Pick<
  NarrativeForm,
  | 'fairValue'
  | 'futureSharesOutstanding'
  | 'discountRatePercentage'
  | 'priceEarningsMultiple'
  | 'profitMarginPercentage'
  | 'revenuePercentage'
  | 'timePeriod'
> => {
  const companyFinancialData = getCompanyFinancialData(company);

  const defaultValuatorData = getDefaultValuatorData({
    ...companyFinancialData,
    fairValue: valuation.fairValue,
    estimateRevenueGrowth: true,
  });

  return {
    /**
     * Fair value can be null for APT/DCF valuations, that is why GQL type has it as nullable.
     * However, these valuation types aren't editable so this defaulting should technically never occur.
     */
    fairValue: valuation.fairValue ?? 0,
    /**
     * Legacy fair values can miss the following fields which were only introduced
     * when we introduced a full page narrative experience with these fields being configurable by the user/backsolve.
     * Prior to this, users could only input fairValue.
     */
    futureSharesOutstanding:
      valuation.futureSharesOutstanding ??
      defaultValuatorData.futureSharesOutstanding,
    discountRatePercentage:
      valuation.discountRate ?? defaultValuatorData.discountRatePercentage,
    priceEarningsMultiple:
      valuation.futurePePercentageChange ??
      defaultValuatorData.priceEarningsMultiple,
    profitMarginPercentage:
      valuation.profitMarginPercentageChange ??
      defaultValuatorData.profitMarginPercentage,
    revenuePercentage:
      valuation.revenuePercentageChange ??
      defaultValuatorData.revenuePercentage,
    timePeriod: valuation.timeframe ?? defaultValuatorData.timePeriod,
  };
};
